/* Public `/changelog` page — SEO-indexed feed of every shipped feature.
 *
 * Why public?
 * 1. SEO: Google indexes each ship → people searching "wholesaling AI
 *    lead scoring" find us.
 * 2. Social proof for podcaster outreach: when Brent Daniels googles
 *    "ispeedtoclose", he sees a thriving, actively-shipping product
 *    instead of a single static landing page.
 * 3. Trust signal: a maintained changelog is a tell that the team is
 *    real and the product is alive. Big SaaS (Linear, Notion, Stripe)
 *    all do this; small SaaS usually doesn't.
 *
 * Mounted at /changelog (NOT under /app/) so it's accessible without auth.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { track, captureUtm } from "@/lib/analytics";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import { Sparkles, ArrowRight, Calendar } from "lucide-react";

const TAG_STYLE = {
  feature: { cls: "bg-emerald-100 text-emerald-800", label: "New" },
  improvement: { cls: "bg-sky-100 text-sky-800", label: "Improved" },
  fix: { cls: "bg-amber-100 text-amber-800", label: "Fixed" },
  announcement: { cls: "bg-violet-100 text-violet-800", label: "Announcement" },
};

export default function PublicChangelog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    captureUtm();
    track("public_changelog_viewed");
    // Set <title> + meta description for SEO. (No react-helmet — keep it light.)
    document.title = "Changelog · iSpeedToClose — Real Estate Wholesaling OS";
    const meta = document.querySelector('meta[name="description"]');
    const desc =
      "See every feature we've shipped to iSpeedToClose — the AI-powered CRM for real estate wholesalers. Lead scoring, property valuation, call summaries, and more.";
    if (meta) meta.setAttribute("content", desc);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = desc;
      document.head.appendChild(m);
    }

    api.get("/changelog/public?limit=100")
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group entries by month for the visual rhythm
  const months = groupByMonth(entries);

  return (
    <div className="mkt-shell bg-[#F8FAFC] min-h-screen" data-testid="public-changelog">
      <Nav />

      <header className="mkt-section py-16 md:py-24 text-center">
        <div className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 rounded-full px-3 py-1 mb-4 shadow-sm">
          <Sparkles size={11} className="text-emerald-600" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
            Changelog
          </span>
        </div>
        <h1 className="font-display font-extrabold text-4xl md:text-6xl text-[var(--ink)] leading-tight">
          Built in the open.
        </h1>
        <p className="text-base md:text-lg text-[var(--ink-2)] mt-4 max-w-2xl mx-auto">
          Every feature we&apos;ve shipped to iSpeedToClose — the AI-powered operating
          system for real estate wholesalers. New things land here every week.
        </p>
        <div className="flex items-center justify-center gap-3 mt-7 flex-wrap">
          <Link
            to="/register"
            data-testid="changelog-cta-register"
            className="btn-money inline-flex items-center gap-1.5"
            onClick={() => track("public_changelog_register_clicked")}
          >
            Start your free trial <ArrowRight size={14} />
          </Link>
          <Link
            to="/"
            className="btn-secondary text-sm"
          >
            See the product →
          </Link>
        </div>
      </header>

      <main className="mkt-section max-w-3xl mx-auto pb-20">
        {loading ? (
          <div className="text-center text-[var(--ink-2)] py-20">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-center text-[var(--ink-2)] py-20">
            We&apos;re shipping fast — check back soon.
          </div>
        ) : (
          months.map(({ key, label, items }) => (
            <section key={key} className="mb-10" data-testid={`changelog-month-${key}`}>
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={14} className="text-[var(--ink-2)]" />
                <h2 className="font-display font-bold text-lg text-[var(--ink-2)]">
                  {label}
                </h2>
                <span className="text-xs text-[var(--ink-2)] ml-2">
                  {items.length} {items.length === 1 ? "ship" : "ships"}
                </span>
              </div>

              <div className="space-y-4">
                {items.map((e) => (
                  <Entry key={e.id} entry={e} />
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      <Footer />
    </div>
  );
}

function Entry({ entry }) {
  const tag = TAG_STYLE[entry.tag] || TAG_STYLE.feature;
  return (
    <article
      className="bg-white rounded-2xl border border-[var(--line)] hover:border-emerald-200 hover:shadow-md transition p-5 md:p-6"
      data-testid={`changelog-entry-public-${entry.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="text-3xl shrink-0 leading-none mt-1">{entry.emoji}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${tag.cls}`}>
              {tag.label}
            </span>
            <span className="text-xs text-[var(--ink-3)]">
              {formatDate(entry.published_at)}
            </span>
          </div>
          <h3 className="font-display font-bold text-lg md:text-xl text-[var(--ink)] leading-snug">
            {entry.title}
          </h3>
          {entry.body && (
            <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
              {entry.body}
            </p>
          )}
          {entry.cta_link && (
            <Link
              to="/register"
              data-testid={`changelog-entry-cta-${entry.id}`}
              className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700 hover:text-emerald-800 mt-3"
            >
              {entry.cta_label || "Try it"} <ArrowRight size={12} />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

function Nav() {
  return (
    <nav className="mkt-section py-5 flex items-center justify-between border-b border-[var(--line)] bg-white/70 backdrop-blur">
      <Link to="/" className="flex items-center gap-2" data-testid="changelog-logo">
        <AssignFlowLogo />
      </Link>
      <div className="flex items-center gap-2 md:gap-4">
        <Link to="/" className="hidden md:inline text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">
          Home
        </Link>
        <Link to="/login" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">
          Sign in
        </Link>
        <Link to="/register" data-testid="changelog-nav-register" className="btn-money text-sm">
          Get started
        </Link>
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-[var(--ink)] text-white/70 py-10">
      <div className="mkt-section flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <AssignFlowLogo />
          <span className="text-xs">© {new Date().getFullYear()} iSpeedToClose</span>
        </div>
        <div className="flex gap-5 text-xs">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/changelog" className="hover:text-white">Changelog</Link>
          <Link to="/legal" className="hover:text-white">Legal</Link>
          <Link to="/login" className="hover:text-white">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function groupByMonth(entries) {
  const groups = new Map();
  for (const e of entries) {
    const d = new Date(e.published_at);
    if (isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    if (!groups.has(key)) groups.set(key, { key, label, items: [] });
    groups.get(key).items.push(e);
  }
  return Array.from(groups.values()); // already sorted desc since entries are
}
